import { Fragment } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  rangeRowErrors,
  rangeGapOverlapWarning,
  type CostByRangeRow,
} from "@/lib/cost-by-range";

// This editor owns ONLY the priceByRange toggle + the range table. The flat
// unit-cost field stays in the host form (RHF number input), disabled when
// priceByRange is on — this avoids a controlled-number decimal-entry regression.
export interface RangePricingValue {
  priceByRange: boolean;
  rows: CostByRangeRow[];
}

export interface RangePricingEditorProps {
  value: RangePricingValue;
  onChange: (next: RangePricingValue) => void;
  disabled?: boolean;
}

const asNum = (s: string) => (s === "" ? 0 : Number(s));

export function RangePricingEditor({ value, onChange, disabled = false }: RangePricingEditorProps) {
  const { priceByRange, rows } = value;
  const warning = priceByRange ? rangeGapOverlapWarning(rows) : null;

  function patchRow(i: number, patch: Partial<CostByRangeRow>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ ...value, rows: next });
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      {/* Toggle */}
      <div className="flex items-center gap-2">
        <input
          id="bi-pricebyrange"
          type="checkbox"
          checked={priceByRange}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, priceByRange: e.target.checked })}
        />
        <Label htmlFor="bi-pricebyrange">Price by range</Label>
      </div>

      {priceByRange && (
        <div className="space-y-2">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ranges yet — add one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th scope="col" className="px-2 py-1 text-left font-medium">From</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">To</th>
                  <th scope="col" className="px-2 py-1 text-left font-medium">Unit cost</th>
                  <th scope="col" className="px-2 py-1"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const errs = rangeRowErrors(r);
                  return (
                    <Fragment key={i}>
                      <tr className="border-b last:border-b-0 align-top">
                        <td className="px-2 py-1">
                          <Input
                            type="number" step="any" aria-label={`From for range ${i + 1}`}
                            disabled={disabled} value={r.from}
                            aria-invalid={errs.includes("from-after-to")}
                            onChange={(e) => patchRow(i, { from: asNum(e.target.value) })}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number" step="any" aria-label={`To for range ${i + 1}`}
                            disabled={disabled} value={r.to}
                            aria-invalid={errs.includes("from-after-to")}
                            onChange={(e) => patchRow(i, { to: asNum(e.target.value) })}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number" step="0.01" aria-label={`Unit cost for range ${i + 1}`}
                            disabled={disabled} value={r.unitCost}
                            aria-invalid={errs.includes("negative")}
                            onChange={(e) => patchRow(i, { unitCost: asNum(e.target.value) })}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Button
                            type="button" variant="ghost" size="icon"
                            aria-label={`Remove range ${i + 1}`} disabled={disabled}
                            onClick={() => onChange({ ...value, rows: rows.filter((_, idx) => idx !== i) })}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                      {errs.length > 0 && (
                        <tr>
                          <td colSpan={4} className="px-2 pb-2">
                            {errs.includes("from-after-to") && (
                              <p className="text-xs text-destructive">From must not exceed To.</p>
                            )}
                            {errs.includes("negative") && (
                              <p className="text-xs text-destructive">Values must not be negative.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {warning && (
            <p role="note" className="text-xs text-amber-700">{warning}</p>
          )}

          <Button
            type="button" variant="outline" size="sm" disabled={disabled}
            onClick={() => onChange({ ...value, rows: [...rows, { from: 0, to: 0, unitCost: 0 }] })}
          >
            Add range
          </Button>
        </div>
      )}
    </div>
  );
}
